import styled, { css } from "styled-components";

import { CURSORS, PNG } from "@ryde/assets";
import {
  DATA_E2E_KEY,
  IStyledAttributes,
  MAP_MARKER_ICON_STYLES,
  routeColorsByIdMapping,
  routeColorsIds,
} from "@ryde/shared";
import { redMui, rydeColors, ScreenDepth } from "@ryde/theme";

/** Detour car pulse — slower than incident rings (1.35s) so they are easy to tell apart. */
const MAP_VEHICLE_DETOUR_PULSE_DURATION = "1.65s";

/**
 * Reads the e2e hook off props with a fallback, rather than hardcoding it. A static `attrs` value BEATS a
 * passed prop in styled-components v6, so hardcoding it silently swallowed the name any screen wrapping this
 * container tried to give its own map (`styled(MapLibreMapHost).attrs({ [DATA_E2E_KEY]: … })`). Screens that
 * pass nothing keep "map-container" exactly as before.
 */
export const StyledMapContainer = styled.div.attrs<IStyledAttributes>(
  props => ({
    [DATA_E2E_KEY]: props[DATA_E2E_KEY] ?? "map-container",
  }),
)<{ $hiddenState?: boolean }>`
  @keyframes ryde-map-vehicle-detour-flash-kf {
    0%,
    100% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
  }

  @keyframes ryde-map-vehicle-detour-shell-scale-kf {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(0.9);
    }
  }

  @keyframes ryde-map-vehicle-detour-pulse-kf {
    0% {
      transform: scale(0.65);
      opacity: 0.92;
    }
    100% {
      transform: scale(1.82);
      opacity: 0;
    }
  }

  @keyframes ryde-map-vehicle-detour-course-glow-kf {
    0%,
    100% {
      filter: drop-shadow(0 0 0 transparent);
    }
    50% {
      filter: drop-shadow(0 0 5px ${redMui._500})
        drop-shadow(0 0 9px ${redMui._400});
    }
  }

  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  z-index: ${ScreenDepth.MAP_CONTAINER};
  ${({ $hiddenState: hiddenState }) =>
    hiddenState &&
    css`
      visibility: hidden;
    `};
  &.polyline-drawing-mode {
    cursor: ${CURSORS.CROSSHAIR_CURSOR_URL};
  }

  .map-marker-simple-icon {
    border-radius: 50%;
    color: ${rydeColors.white};
    font-size: 14px;
    position: relative;
    text-align: center;
    padding-top: 4px;
    font-weight: 600;

    ${routeColorsIds().map(
      routeColorId => `
      &.map-marker-simple-icon-${routeColorId} {
        background-color: ${routeColorsByIdMapping[routeColorId]};

        &::after {
          border-top-color: ${routeColorsByIdMapping[routeColorId]};
        }
      }
    `,
    )}

    .map-marker-simple-icon-shell {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${rydeColors.white};
    }

    &.map-marker-simple-icon--detour {
      overflow: visible;
      background: transparent;
      box-shadow: none;

      .map-marker-detour-wrap {
        position: relative;
        width: 30px;
        height: 30px;
        overflow: visible;
      }

      .map-marker-detour-wrap__pulse {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        border: 3px solid ${redMui._400}eb;
        box-sizing: border-box;
        z-index: 0;
        pointer-events: none;
        filter: drop-shadow(0 0 6px ${redMui._400}e8)
          drop-shadow(0 0 10px ${redMui._300}cc);
        animation: ryde-map-vehicle-detour-pulse-kf
          ${MAP_VEHICLE_DETOUR_PULSE_DURATION} ease-in-out infinite;
      }

      .map-marker-detour-shell {
        position: relative;
        z-index: 1;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        transform-origin: center center;
        animation: ryde-map-vehicle-detour-shell-scale-kf
          ${MAP_VEHICLE_DETOUR_PULSE_DURATION} ease-in-out infinite;

        .map-marker-detour-shell__flash {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          pointer-events: none;
          z-index: 0;
          animation: ryde-map-vehicle-detour-flash-kf
            ${MAP_VEHICLE_DETOUR_PULSE_DURATION} ease-in-out infinite;
        }

        .map-marker-vehicle-car {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${rydeColors.white};
        }
      }
    }
  }

  .map-marker-icon.map-marker-icon--course-code {
    background: transparent !important;
    border: none !important;
    z-index: ${ScreenDepth.MAP_COURSE_CODE_MARKER} !important;
  }

  /* Detour on a course (numbered balloon) ride: pulse ring + gentle scale + red
     glow flash — mirrors the off-route car indicator's pulsing effect. */
  .map-marker-icon.map-marker-icon--course-code.map-marker-icon--detour {
    overflow: visible;

    .map-marker-icon-course-pin-wrap {
      overflow: visible;
      transform-origin: 50% 100%;
      animation: ryde-map-vehicle-detour-shell-scale-kf
        ${MAP_VEHICLE_DETOUR_PULSE_DURATION} ease-in-out infinite;
    }

    .map-marker-icon-course-pin-svg {
      position: relative;
      z-index: ${ScreenDepth.MAP_COURSE_MARKER_PIN};
      animation: ryde-map-vehicle-detour-course-glow-kf
        ${MAP_VEHICLE_DETOUR_PULSE_DURATION} ease-in-out infinite;
    }

    .map-marker-icon-text {
      z-index: ${ScreenDepth.MAP_COURSE_MARKER_LABEL};
    }

    .map-marker-icon-course-pin-pulse {
      position: absolute;
      top: 40%;
      left: 50%;
      width: 30px;
      height: 30px;
      margin-top: -15px;
      margin-left: -15px;
      border-radius: 50%;
      border: 3px solid ${redMui._400}eb;
      box-sizing: border-box;
      z-index: ${ScreenDepth.MAP_COURSE_MARKER_DETOUR_PULSE};
      pointer-events: none;
      filter: drop-shadow(0 0 6px ${redMui._400}e8)
        drop-shadow(0 0 10px ${redMui._300}cc);
      animation: ryde-map-vehicle-detour-pulse-kf
        ${MAP_VEHICLE_DETOUR_PULSE_DURATION} ease-in-out infinite;
    }
  }

  /* Respect users who prefer reduced motion: keep the detour readable as a static
     red ring / glow but stop the animation (WCAG 2.1 AA). */
  @media (prefers-reduced-motion: reduce) {
    .map-marker-simple-icon--detour .map-marker-detour-wrap__pulse,
    .map-marker-simple-icon--detour .map-marker-detour-shell,
    .map-marker-simple-icon--detour .map-marker-detour-shell__flash,
    .map-marker-icon--course-code.map-marker-icon--detour
      .map-marker-icon-course-pin-wrap,
    .map-marker-icon--course-code.map-marker-icon--detour
      .map-marker-icon-course-pin-svg,
    .map-marker-icon--course-code.map-marker-icon--detour
      .map-marker-icon-course-pin-pulse {
      animation: none !important;
    }

    .map-marker-simple-icon--detour .map-marker-detour-shell__flash,
    .map-marker-icon--course-code.map-marker-icon--detour
      .map-marker-icon-course-pin-pulse {
      opacity: 1;
    }
  }

  .map-marker-icon {
    width: 100%;
    height: 100%;
    ${routeColorsIds().map(
      routeColorId => `
      &.map-marker-icon-${routeColorId} {
        .map-marker-icon-inner, .map-marker-icon-badge {
          background-color: ${routeColorsByIdMapping[routeColorId]};
        }
      }
    `,
    )}
  }
  ${MAP_MARKER_ICON_STYLES}

  .popup-content {
    text-align: center;
    font-size: 14px;
    color: ${rydeColors.darkBlue};
  }

  .popup-items {
    list-style: none;
  }

  .hidden-item {
    display: none;
  }

  .show-more-btn {
    background: none;
    border: none;
    margin-top: 5px;
    color: ${rydeColors.grey};
  }

  .show-more-btn:hover {
    background: none;
    cursor: pointer;
    text-decoration: underline;
  }
  .custom-tracking-tooltip {
    /* Force LTR so Leaflet anchors the tooltip at the hovered point.
       Under the app's dir="rtl", the default tooltip (no explicit left)
       anchors to the right edge of the map pane and lands far from the point. */
    direction: ltr;
    white-space: nowrap;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
    background-color: white;
    border-radius: 6px;
    padding: 4px 10px;

    .custom-tracking-tooltip__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
    }

    .custom-tracking-tooltip__time {
      font-size: 15px;
      font-weight: 600;
      color: ${rydeColors.darkBlue};
    }

    .custom-tracking-tooltip__speed {
      font-size: 13px;
      color: ${rydeColors.grey};
    }

    .custom-tracking-tooltip__gap {
      margin-top: 2px;
      font-size: 11px;
      font-style: italic;
      color: ${rydeColors.grey};
    }
  }

  .google-maps-logo {
    position: absolute;
    bottom: 5px;
    left: 10px;
    height: 16px;
    z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};
    display: flex;
    align-items: center;

    svg {
      height: 16px;
      width: auto;
    }
  }

  .google-maps-attribution {
    position: absolute;
    bottom: 5px;
    right: 10px;
    background: rgba(255, 255, 255, 0.8);
    padding: 2px 4px;
    border-radius: 2px;
    font-size: 10px;
    color: ${rydeColors.googleAttributes};
    z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};
    max-width: 200px;
    word-wrap: break-word;
  }
`;

export const StyledMapPlaceholderNoData = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "map-placeholder-no-data",
})`
  width: 100%;
  height: 100%;
  background: url(${PNG.GlobeBgPNG}) no-repeat;
  background-size: cover;
`;

export const SETTEXT_POLYLINE_ARROW_SYMBOL = "►";
export const SETTEXT_POLYLINE_ARROW_SYMBOL_GAP_NUMBER = 24;
export const SETTEXT_POLYLINE_ARROW_SYMBOL_GAPS = " ".repeat(
  SETTEXT_POLYLINE_ARROW_SYMBOL_GAP_NUMBER + 1,
);
export const SETTEXT_POLYLINE_ARROW_SYMBOL_WITH_GAPS = `${SETTEXT_POLYLINE_ARROW_SYMBOL}${SETTEXT_POLYLINE_ARROW_SYMBOL_GAPS}`;
// Constants for polyline drawing
export const DRAW_POLYLINE_WEIGHT_ACTIVE = 4;
export const DRAW_POLYLINE_WEIGHT_INACTIVE = 3;
export const DRAW_POLYLINE_OPACITY_ACTIVE = 0.3;
export const DRAW_POLYLINE_OPACITY_INACTIVE = 0.6;
export const DRAW_POLYLINE_MIN_POINTS = 3;
export const DRAW_POLYLINE_DASH_ARRAY = "5, 5";
export const DRAW_POLYLINE_POINT_RADIUS = 5;
// Editing a road-following route: simplify to fewer, spread-out handles.
// Larger = fewer points (pixel tolerance for Douglas-Peucker at current zoom).
export const POLYLINE_EDIT_SIMPLIFY_TOLERANCE = 12;
